/**
 * PRISERNAS DATALAGER — det Timpriser-skärmen läser och skriver genom.
 *
 * =========================================================================
 * DET FINNS INGEN PRISRUTT, OCH DET ÄR MED FLIT.
 * shared/routes.ts: SYNC_PUSH och SYNC_PULL är den ENDA kanalen mot
 * företagsdata — "inga skriv-endpoints per tabell", och ingen läs-endpoint
 * heller. Prislistan, prisraderna och firmans inställningar hämtas därför ur
 * samma SYNC_PULL som allt annat, och ändras genom SYNC_PUSH.
 * Steg 5 äger den riktiga lagringen (IndexedDB, utkorg, bakgrundssynk). Tills
 * dess hämtar den här modulen HELA firman från since = 0 och håller resultatet
 * i minnet — medvetet enkelt och medvetet tillfälligt. Allt ligger bakom
 * `useTimprislager`, så den dag lagret finns byts innanmätet utan att skärmen
 * rör sig.
 * =========================================================================
 *
 * TVÅ RADER SOM REDAN FINNS, OCH SOM ALDRIG SKA SKAPAS OM.
 *   1. STANDARDPRISLISTAN. CREATE_COMPANY bär `default_price_list_id` och
 *      seedCompany (worker/src/do/company-do.ts) lägger upp listan TOM med
 *      is_default = 1. Skärmen letar alltså upp den — den skapar den aldrig.
 *      Två levande listor med is_default = 1 är dessutom omöjligt
 *      (price_list_one_default i schema/company.sql).
 *   2. FIRMANS INSTÄLLNINGAR. company_settings seedas av SCHEMAT med id 1,
 *      version 1, milpris 0 och moms 2500. Den kan inte raderas
 *      (CHECK (deleted_at IS NULL)) och ska aldrig skickas med base_version
 *      null — det betyder "raden ska inte finnas" och ger SYNC_CONFLICT direkt.
 *      Skicka radens NUVARANDE version, alltid.
 *
 * PRISRADEN HAR EN UNIK NYCKEL: (price_list_id, role) bland levande rader
 * (price_rate_key). En NY rad för en roll som redan har ett pris ger därför
 * ROW_DUPLICATE, inte en andra rad. Därför bär varje ändring nedan radens EGET
 * id och EGEN version när raden finns, och ett nytt id med base_version null
 * bara när den inte gör det.
 */

import { useCallback, useEffect, useState } from 'react';
import { SYNC_PULL, SYNC_PUSH } from '@delat/routes';
import { SYNC_PULL_MAX_LIMIT, asMillis, asSeq } from '@delat/types';
import type {
  ClientView,
  CompanyId,
  CompanySettingsRow,
  Hundredths,
  Millis,
  OpId,
  OrePer10Km,
  OrePerHour,
  PriceListId,
  PriceListRow,
  PriceRateId,
  PriceRateRow,
  PriceRole,
  Seq,
  SyncOp,
  SyncPullResponse,
  SyncPushResponse,
} from '@delat/types';
import { apifel, enhetsId, granska, klienten, lyckades, misslyckades, nyUlid } from '../api';
import type { Klartidfel, Resultat } from '../api';
import { granskaSyncPull, granskaSyncPush, prisradId } from '../api/granskare/priser';
import { registreraFirmaminne } from '../app/firmaminne';

/* == CHECK-gränserna ur schema/company.sql ===============================
   Servern svarar VALIDATION_FAILED { field } på ett brott mot dem, alltså ett
   fel EFTER att chefen tryckt Spara. Klienten bär samma tal för att kunna säga
   det INNAN — aldrig för att ersätta serverns kontroll.
   DE TRE TALEN STÅR I api/konstanter.ts, bredvid de andra avskrivna
   CHECK-gränserna, och test/klienten.ts läser CHECK-raderna ur
   schema/company.sql och jämför. Raderna nedan är re-exporter så att skärmen
   inte behöver två importvägar för samma sak.
   ======================================================================= */

export {
  MAX_MOMS_HUNDRADELAR,
  MAX_ORE_PER_MIL,
  MAX_ORE_PER_TIMME,
} from '../api/konstanter';

/* == Lagret ============================================================== */

export interface Prislager {
  readonly vy: ClientView;
  /**
   * Firmans standardlista (is_default = 1). null betyder att svaret inte bar
   * den — antingen för att vyn inte är 'full' (beslut 16) eller för att något
   * är fel i firman. Skärmen säger det rakt ut i stället för att skapa en ny.
   */
  readonly prislista: PriceListRow | null;
  /** Standardlistans prisrader, en per roll. Högst fyra. */
  readonly prisrader: readonly PriceRateRow[];
  /** company_settings id 1. null av samma skäl som prislistan. */
  readonly installningar: CompanySettingsRow | null;
  /**
   * true = firman har fler rader än MAX_SIDOR * SYNC_PULL_MAX_LIMIT och allt
   * kom inte med. Skärmen SKA säga det: ett timpris som saknas i listan utan
   * att något felade är värre än ett felmeddelande.
   */
  readonly avkortat: boolean;
}

/** Rad med de två synkkolumner sammanslagningen behöver. */
interface Synkrad {
  readonly seq: Seq;
  readonly deleted_at: Millis | null;
}

interface Samlare {
  vy: ClientView;
  readonly listor: Map<string, PriceListRow>;
  readonly priser: Map<string, PriceRateRow>;
  installningar: CompanySettingsRow | null;
}

const tomSamlare = (): Samlare => ({
  vy: 'worker',
  listor: new Map(),
  priser: new Map(),
  installningar: null,
});

/**
 * Högsta seq vinner. En pull som görs i flera vändor kan få samma rad två
 * gånger om någon skriver mitt i, och då är den med högst seq den senaste
 * (schema/company.sql: varje accepterad skrivning får ett nytt seq).
 */
const slaSamman = <T extends Synkrad & { readonly id: string }>(
  karta: Map<string, T>,
  rader: readonly T[],
): void => {
  for (const rad of rader) {
    const gammal = karta.get(rad.id);
    if (gammal === undefined || rad.seq > gammal.seq) karta.set(rad.id, rad);
  }
};

/** Mjukraderade rader kommer med i pullen just för att kunna tas bort här. */
const levande = <T extends Synkrad>(karta: Map<string, T>): readonly T[] =>
  [...karta.values()].filter((rad) => rad.deleted_at === null);

const lagg = (s: Samlare, svar: SyncPullResponse): void => {
  s.vy = svar.view;
  // BESLUT 16 I EN ENDA RAD: prisdata finns bara i den fulla vyn. Montörens och
  // gästens ändringsmängder har inte ens fälten, så det finns ingenting att
  // plocka ur — och tsc släpper inte in ett försök.
  if (svar.view !== 'full') return;
  const c = svar.changes;
  slaSamman(s.listor, c.price_list);
  slaSamman(s.priser, c.price_rate);
  for (const rad of c.company_settings) {
    if (s.installningar === null || rad.seq > s.installningar.seq) s.installningar = rad;
  }
};

const fardigt = (s: Samlare, avkortat: boolean): Prislager => {
  const prislista = levande(s.listor).find((l) => l.is_default === 1) ?? null;
  const prisrader =
    prislista === null
      ? []
      : levande(s.priser)
          .filter((p) => p.price_list_id === prislista.id)
          .slice()
          .sort((a, b) => a.role.localeCompare(b.role, 'sv'));
  return {
    vy: s.vy,
    prislista,
    prisrader,
    installningar: s.installningar,
    avkortat,
  };
};

/* == Hämtningen ========================================================== */

/**
 * Taket för hur många vändor en hämtning tar. 40 * 1000 rader räcker långt för
 * en firma med fem man; blir det fler säger `avkortat` det rakt ut i stället
 * för att listan tyst blir ofullständig.
 */
const MAX_SIDOR = 40;

const hamtaAllt = async (
  firmaId: CompanyId,
  signal: AbortSignal,
): Promise<Resultat<Prislager>> => {
  const samlare = tomSamlare();
  let since = asSeq(0);
  let avkortat = false;

  for (let varv = 0; varv < MAX_SIDOR; varv += 1) {
    const svar = await klienten.anrop(
      SYNC_PULL,
      {
        params: { company_id: firmaId },
        data: { since, limit: SYNC_PULL_MAX_LIMIT },
        signal,
      },
      granskaSyncPull,
    );
    if (!svar.ok) return misslyckades(svar.fel);
    lagg(samlare, svar.varde);

    // has_more = true betyder BARA att fler seq återstår, inte att den här
    // vändan gav rader (SyncPullResponse). En tom vända med has_more är NORMAL
    // och måste följas av en ny pull på next_since.
    if (!svar.varde.has_more) break;
    if (svar.varde.next_since <= since) {
      // MARKÖREN STÅR STILL MEDAN SERVERN SÄGER has_more: vi vet att det finns
      // rader vi inte fick. Att bryta tyst hade ritat en halv prislista som om
      // den vore hel — värre än ett felmeddelande. Flaggan sätts därför här
      // också, inte bara när taket nås.
      avkortat = true;
      break;
    }
    since = svar.varde.next_since;
    avkortat = varv === MAX_SIDOR - 1;
  }

  return lyckades(fardigt(samlare, avkortat));
};

/* == Ett litet minne ====================================================
   Priserna sätts en gång och ändras sedan sällan (appboken: "En gång, sedan
   sällan"), men skärmen öppnas om varje gång chefen backar och går in igen.
   Minnet är frivilligt: `glomPrislagret()` efter varje skrivning, och en
   hämtning äldre än FARSKHET_MS görs om ändå.
   ======================================================================= */

const FARSKHET_MS = 30_000;

let minne: { firmaId: CompanyId; lager: Prislager; hamtat: number } | null = null;

export const glomPrislagret = (): void => {
  minne = null;
};

/**
 * MINNET ÄR NYCKLAT PÅ FIRMAN OCH INTE PÅ KONTOT, så utan den här raden hade en
 * utloggning lämnat kvar hela firmans prisbild i modulen: nästa inloggning i
 * samma flik och samma firma hade fått föregående användares priser serverade ur
 * minnet i upp till FARSKHET_MS, utan ett enda anrop. Se app/firmaminne.ts.
 */
registreraFirmaminne(glomPrislagret);

export type Prislage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klart'; readonly lager: Prislager };

export interface Prisstyrning {
  readonly lage: Prislage;
  /** Hämtar om, alltid mot nätet. Efter en skrivning eller på "Försök igen". */
  readonly laddaOm: () => void;
}

export const useTimprislager = (firmaId: CompanyId): Prisstyrning => {
  const [lage, sattLage] = useState<Prislage>(() =>
    minne !== null && minne.firmaId === firmaId && Date.now() - minne.hamtat < FARSKHET_MS
      ? { status: 'klart', lager: minne.lager }
      : { status: 'laddar' },
  );
  const [omgang, sattOmgang] = useState(0);

  const laddaOm = useCallback(() => {
    glomPrislagret();
    sattLage({ status: 'laddar' });
    sattOmgang((n) => n + 1);
  }, []);

  useEffect(() => {
    if (minne !== null && minne.firmaId === firmaId && Date.now() - minne.hamtat < FARSKHET_MS) {
      sattLage({ status: 'klart', lager: minne.lager });
      return;
    }
    const styrning = new AbortController();
    let vaken = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await hamtaAllt(firmaId, styrning.signal);
      if (!vaken) return;
      if (!svar.ok) {
        // Ett avbrutet anrop är inte ett fel — skärmen lämnades.
        if (svar.fel.slag === 'nat' && svar.fel.avbrutet) return;
        sattLage({ status: 'fel', fel: svar.fel });
        return;
      }
      minne = { firmaId, lager: svar.varde, hamtat: Date.now() };
      sattLage({ status: 'klart', lager: svar.varde });
    })();

    return () => {
      vaken = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  return { lage, laddaOm };
};

/* == Uppslag skärmen gör om och om igen ================================== */

export const prisForRoll = (lager: Prislager, roll: PriceRole): PriceRateRow | null =>
  lager.prisrader.find((rad) => rad.role === roll) ?? null;

/* == Skrivningen ========================================================= */

/**
 * OP_ID ÄR ANROPARENS (beslut 09). Mynta ETT när chefen trycker Spara, spara det
 * med det du ska skicka och använd SAMMA vid varje omsändning — annars blir
 * varje omtag en ny skrivning i stället för ett uppspelat svar.
 */
export const nyttOpId = (): OpId => granska.opId(nyUlid(), 'op_id');
export const nyttPrisradId = (): PriceRateId => prisradId(nyUlid(), 'price_rate.id');
export const nu = (): Millis => asMillis(Date.now());

/**
 * ETT NYTT TIMPRIS FÖR EN ROLL. base_version null = "raden ska inte finnas",
 * vilket alltid ger version 1. Finns raden redan svarar servern SYNC_CONFLICT,
 * och finns det redan ett levande pris för rollen ROW_DUPLICATE — därför används
 * den här bara när `prisForRoll` gav null.
 */
export const nyttPrisOp = (
  prislista: PriceListId,
  roll: PriceRole,
  orePerTimme: OrePerHour,
): { readonly op: SyncOp; readonly id: PriceRateId } => {
  const id = nyttPrisradId();
  return {
    id,
    op: {
      op_id: nyttOpId(),
      client_time: nu(),
      kind: 'put',
      base_version: null,
      table: 'price_rate',
      id,
      row: { price_list_id: prislista, role: roll, ore_per_hour: orePerTimme },
    },
  };
};

/** ÄNDRAT TIMPRIS. Radens NUVARANDE version som base_version, aldrig null. */
export const andratPrisOp = (rad: PriceRateRow, orePerTimme: OrePerHour): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'put',
  base_version: rad.version,
  table: 'price_rate',
  id: rad.id,
  row: { price_list_id: rad.price_list_id, role: rad.role, ore_per_hour: orePerTimme },
});

/**
 * BORTTAGET TIMPRIS — fältet tömdes. Raden mjukraderas, och rollen har därmed
 * inget pris: nästa tidrad för en person med den rollen får PRICE_MISSING och
 * sparas inte alls. Skärmen skriver ut det.
 * En prisrad får raderas fritt även om tidrader fryst sitt pris ur den
 * (schema/company.sql) — gamla rader ändrar sig aldrig.
 */
export const borttagetPrisOp = (rad: PriceRateRow): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'delete',
  base_version: rad.version,
  table: 'price_rate',
  id: rad.id,
});

/**
 * FIRMANS EGNA PRISER. id är TALET 1 och inget id-märke — company_settings är
 * den enda tabellen med ett heltal som nyckel (shared/types.ts: SyncPut bär
 * `{ table: 'company_settings'; id: 1 }`). Raden finns alltid, så base_version
 * är dess nuvarande version.
 */
export const installningarOp = (
  rad: CompanySettingsRow,
  milprisOren: OrePer10Km,
  momsHundradelar: Hundredths,
): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'put',
  base_version: rad.version,
  table: 'company_settings',
  id: 1,
  row: { mileage_ore_per_10km: milprisOren, vat_rate_hundredths: momsHundradelar },
});

export const skickaOps = async (
  firmaId: CompanyId,
  ops: readonly SyncOp[],
  signal?: AbortSignal,
): Promise<Resultat<SyncPushResponse>> =>
  klienten.anrop(
    SYNC_PUSH,
    {
      params: { company_id: firmaId },
      data: { device_id: enhetsId(), ops },
      ...(signal === undefined ? {} : { signal }),
    },
    granskaSyncPush,
  );

/**
 * FÖRSTA OPERATIONEN SOM FÖLL, som ett fel gränssnittet kan visa.
 *
 * Ett giltigt kuvert svarar ALLTID 200 även om varje operation föll, så
 * `svar.ok` säger ingenting om utfallet — det står i SyncOpResult. Texten hämtas
 * ur shared/errors.ts genom apifel() och ALDRIG ur serverns sträng (api/fel.ts).
 */
export const forstaMisslyckade = (svar: SyncPushResponse): Klartidfel | null => {
  for (const resultat of svar.results) {
    if (resultat.status !== 'failed') continue;
    const kropp = resultat.error;
    // Grenarna finns för att de två SLUTNA detaljformerna ska behålla sin typ
    // hela vägen in i apifel(); se ApifelFor i api/fel.ts.
    if (kropp.code === 'ROW_INVOICED') {
      return apifel('ROW_INVOICED', kropp.detail, kropp.message_sv);
    }
    if (kropp.code === 'PRICE_MISSING') {
      return apifel('PRICE_MISSING', kropp.detail, kropp.message_sv);
    }
    return apifel(kropp.code, kropp.detail, kropp.message_sv);
  }
  return null;
};

/** true = operationen är gjord och ska INTE skickas igen ur samma plan. */
export const arKlar = (svar: SyncPushResponse, op: OpId): boolean =>
  svar.results.some((r) => r.op_id === op && (r.status === 'applied' || r.status === 'duplicate'));
